import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Maximize2, Minimize2, BookmarkPlus, CheckCircle, AlertTriangle, Sun, Moon } from 'lucide-react';
import { demoQuestions } from '../../data/demoQuestions';
import { useDemoTheme } from './DemoThemeContext';

function formatTime(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export default function TopNav({ currentQuestion, timeLeft, isFullscreen, onToggleFullscreen, onSubmit, onToggleReview, questionStatus, showAutoSave }) {
  const { isDark, toggle } = useDemoTheme();
  const [showConfirm, setShowConfirm] = useState(false);
  const question = demoQuestions[currentQuestion];

  const timerColor = timeLeft < 60 ? '#ef4444' : timeLeft < 300 ? '#f59e0b' : '#22c55e';
  const isPulse = timeLeft < 60;
  const isReview = question && questionStatus[question.id] === 'review';

  return (
    <>
      <nav
        data-testid="exam-topnav"
        className="flex items-center justify-between px-5 h-14 flex-shrink-0"
        style={{
          background: 'var(--d-nav)',
          borderBottom: '1px solid var(--d-border)',
          position: 'relative',
          zIndex: 10,
          boxShadow: isDark ? 'none' : '0 1px 12px rgba(124,58,237,0.06)',
        }}
      >
        {/* Left */}
        <div className="flex items-center gap-4 min-w-0">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}>LQ</div>
            <span className="text-xs font-semibold hidden sm:block" style={{ color: 'var(--d-text-1)', letterSpacing: '0.05em' }}>Assessment</span>
          </div>
          <div className="w-px h-4 hidden sm:block" style={{ background: 'var(--d-border)' }} />
          <div className="min-w-0 hidden md:flex items-center gap-2">
            <span className="text-xs font-mono" style={{ color: 'var(--d-text-3)' }}>Q{question?.id}/{demoQuestions.length}</span>
            <span className="text-xs truncate max-w-[200px]" style={{ color: 'var(--d-text-2)' }}>{question?.title}</span>
          </div>
        </div>

        {/* Center: auto-save */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <AnimatePresence>
            {showAutoSave && (
              <motion.div key="autosave" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                className="flex items-center gap-1.5 text-xs font-mono" style={{ color: '#22c55e' }}>
                <CheckCircle size={12} />Progress Saved
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right */}
        <div className="flex items-center gap-1.5">
          {/* Timer */}
          <div data-testid="exam-timer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-sm font-semibold"
            style={{ background: `${timerColor}12`, border: `1px solid ${timerColor}30`, color: timerColor }}>
            <motion.div animate={isPulse ? { scale: [1, 1.15, 1], opacity: [1, 0.7, 1] } : {}} transition={isPulse ? { duration: 0.8, repeat: Infinity } : {}}
              className="w-1.5 h-1.5 rounded-full" style={{ background: timerColor }} />
            {formatTime(timeLeft)}
          </div>

          {/* Flag */}
          <button data-testid="flag-review-btn" onClick={() => question && onToggleReview(question.id)}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
            style={{
              background: isReview ? 'rgba(245,158,11,0.15)' : 'var(--d-hover)',
              border: isReview ? '1px solid rgba(245,158,11,0.4)' : '1px solid var(--d-border)',
              color: isReview ? '#fbbf24' : 'var(--d-text-2)',
            }}
            title="Flag for Review (Alt+R)"><BookmarkPlus size={14} /></button>

          {/* Theme toggle */}
          <button
            data-testid="demo-theme-toggle"
            onClick={toggle}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200"
            style={{
              background: 'var(--d-hover)',
              border: '1px solid var(--d-border)',
              color: 'var(--d-text-2)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#7c3aed'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--d-text-2)'; e.currentTarget.style.borderColor = 'var(--d-border)'; }}
            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDark ? <Sun size={13} /> : <Moon size={13} />}
          </button>

          {/* Fullscreen */}
          <button data-testid="fullscreen-toggle-btn" onClick={onToggleFullscreen}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
            style={{ background: 'var(--d-hover)', border: '1px solid var(--d-border)', color: 'var(--d-text-2)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--d-text-hi)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--d-text-2)')}
            title="Toggle Fullscreen">
            {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>

          {/* Submit */}
          <button data-testid="topnav-submit-btn" onClick={() => setShowConfirm(true)}
            className="hidden sm:flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90 text-white"
            style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}>
            Submit
          </button>
        </div>
      </nav>

      {/* Submit Confirm */}
      <AnimatePresence>
        {showConfirm && (
          <motion.div key="confirm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }}>
            <motion.div initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 12 }}
              className="w-full max-w-sm rounded-2xl p-7" data-testid="submit-confirm-modal"
              style={{ background: 'var(--d-surface)', border: '1px solid var(--d-border-str)', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.2)' }}>
                <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
              </div>
              <h3 className="font-semibold text-base mb-2" style={{ color: 'var(--d-text-hi)' }}>Submit Assessment?</h3>
              <p className="text-sm mb-6 leading-relaxed" style={{ color: 'var(--d-text-2)' }}>
                Once submitted, you cannot change your answers. Remaining time:{' '}
                <span style={{ color: timerColor }} className="font-mono font-semibold">{formatTime(timeLeft)}</span>.
              </p>
              <div className="flex gap-3">
                <button data-testid="confirm-cancel-btn" onClick={() => setShowConfirm(false)}
                  className="flex-1 rounded-xl py-2.5 text-sm font-medium transition-colors"
                  style={{ background: 'var(--d-hover)', border: '1px solid var(--d-border)', color: 'var(--d-text-1)' }}>
                  Continue
                </button>
                <button data-testid="confirm-submit-btn" onClick={() => { setShowConfirm(false); onSubmit(); }}
                  className="flex-1 rounded-xl py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}>
                  Submit Now
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
